/**
 * useLongPress - Detects long-press gestures on touch/mouse events
 * Returns handlers for onPointerDown, onPointerUp, onPointerLeave, onPointerCancel
 */

import { useCallback, useEffect, useRef } from 'react';

export interface UseLongPressOptions {
  /** Duration in ms to trigger long press (default: 500ms) */
  threshold?: number;
  /** Callback when long press is detected */
  onLongPress: () => void;
  /** Callback for regular click (when released before threshold) */
  onClick?: () => void;
  /** Disable the long press handler */
  disabled?: boolean;
}

export interface UseLongPressHandlers {
  onPointerDown: (e: React.PointerEvent) => void;
  onPointerUp: (e: React.PointerEvent) => void;
  onPointerLeave: (e: React.PointerEvent) => void;
  onPointerCancel: (e: React.PointerEvent) => void;
  onContextMenu: (e: React.MouseEvent) => void;
}

export function useLongPress({
  threshold = 500,
  onLongPress,
  onClick,
  disabled = false,
}: UseLongPressOptions): UseLongPressHandlers {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isLongPressTriggered = useRef(false);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const handlePointerDown = useCallback(
    (_e: React.PointerEvent) => {
      if (disabled) return;

      isLongPressTriggered.current = false;

      timerRef.current = setTimeout(() => {
        isLongPressTriggered.current = true;
        onLongPress();
      }, threshold);
    },
    [disabled, onLongPress, threshold]
  );

  const handlePointerUp = useCallback(
    (_e: React.PointerEvent) => {
      clearTimer();

      // If long press wasn't triggered and we have an onClick handler, call it
      if (!disabled && !isLongPressTriggered.current && onClick) {
        onClick();
      }
    },
    [clearTimer, onClick, disabled]
  );

  const handlePointerLeave = useCallback(() => {
    clearTimer();
  }, [clearTimer]);

  const handlePointerCancel = useCallback(() => {
    clearTimer();
  }, [clearTimer]);

  // Prevent context menu on long press (especially on mobile)
  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    if (isLongPressTriggered.current) {
      e.preventDefault();
    }
  }, []);

  // Clear timer on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      clearTimer();
    };
  }, [clearTimer]);

  // Clear timer when disabled changes to true
  useEffect(() => {
    if (disabled) {
      clearTimer();
    }
  }, [disabled, clearTimer]);

  return {
    onPointerDown: handlePointerDown,
    onPointerUp: handlePointerUp,
    onPointerLeave: handlePointerLeave,
    onPointerCancel: handlePointerCancel,
    onContextMenu: handleContextMenu,
  };
}

export default useLongPress;
